import React, { lazy } from 'react'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { SidebarProvider } from 'context/sidebar.js'
import { FilterModalProvider } from 'context/filter-modal.js'
import PrimeToastProvider from './components/common/toast/PrimeToastProvider.jsx'
import ExecutiveReportJobTracker from './components/common/executiveReportJobTracker.jsx'
import ContactsSyncJobTracker from './components/common/contactsSyncJobTracker.jsx'
import TruncationTooltip from './components/common/truncationTooltip.jsx'
import Login from './pages/login/index.jsx'
import PrivateRoute from './utils/PrivateRoute'
import { isAccessTokenValid } from './utils/authToken'
import RouteLoadingBoundary from './components/common/routeLoadingBoundary'
// Shared controls and dialogs need the theme before any deferred route loads.
import 'primereact/resources/themes/lara-light-cyan/theme.css'
import './styles.scss'
import { REACT_APP_AUTH_API_BASE_URL } from './api/axiosApiClient'
import { resolveOAuthCallbackForwardUrl, toOrigin } from './utils/oauthCallbackForwarding'

const Home = lazy(() => import('./pages/home/index.jsx'))
const Signup = lazy(() => import('./pages/signup/index.jsx'))
const New = lazy(() => import('./pages/new/index.jsx'))
const OAuthConsent = lazy(() => import('./components/auth/oauthConsent/index.jsx'))

function ProtectedHome() {
  // Redirect guests before requesting the authenticated application chunk.
  if (!isAccessTokenValid()) {
    return <PrivateRoute><></></PrivateRoute>
  }
  return <Home />
}

function getApiOrigin() {
  const candidates = [
    process.env.REACT_APP_INTEGRATIONS_API_BASE_URL,
    process.env.REACT_APP_BASE_URL,
    process.env.REACT_APP_API_URL,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  for (const candidate of candidates) {
    try {
      return new URL(candidate).origin
    } catch (_error) {
      // ignore invalid candidate
    }
  }

  return ''
}

function App() {
  if (typeof window !== 'undefined') {
    const nextUrl = resolveOAuthCallbackForwardUrl({
      pathname: window.location.pathname,
      search: window.location.search,
      currentOrigin: window.location.origin,
      authApiOrigin: toOrigin(REACT_APP_AUTH_API_BASE_URL),
      integrationsApiOrigin: getApiOrigin(),
    })
    if (nextUrl) {
      window.location.replace(nextUrl)
      return null
    }
  }

  return (
    <Router>
      <SidebarProvider>
        <FilterModalProvider>
          <div>
            <RouteLoadingBoundary>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/new" element={<New />} />
                <Route path="/oauth/consent" element={<OAuthConsent />} />
                <Route path="*" element={<ProtectedHome />} />
              </Routes>
            </RouteLoadingBoundary>
            <PrimeToastProvider />
            <ExecutiveReportJobTracker />
            <ContactsSyncJobTracker />
            <TruncationTooltip />
          </div>
        </FilterModalProvider>
      </SidebarProvider>
    </Router>
  )
}

export default App
